import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import AppTitle from '../../../layouts/AppTitle';
import { aboutBg, shopBg } from '../../../../assets/images';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getPageContent } from '../../../../store/actions/ProductActions';
import { resetActionValues } from '../../../../store/actions/AppActions';
import { addItemToCart } from '../../../../store/actions/CartActions';
import AppLoader from '../../../pages/AppLoader';
import { endpoint } from '../../../../config/config';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel, Pagination, Autoplay } from 'swiper/modules';
import ProductCard from './ProductCard';
import moment from 'moment';

import ImageGallery from 'react-image-gallery';
import { Tooltip } from '@mui/material';
import { toast } from 'react-toastify';
import {
  appMultipleOptions,
  customSelectTitles,
  onlyWhatsappOrders,
} from '../../../../utils/appConstants';
import { calculateExpectedDeliveryDate, priceFormatter } from '../../../../utils/appFunctions';
import renderElements from '../../../../utils/renderElements';
import { specialCakeNote } from '../../../../utils/formElements';

const ProductDetails = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const { id } = params;
  const navigate = useNavigate();
  const myDivRef = useRef(null);

  const productDetails = useSelector(state => state?.products?.productDetails?.product);
  const cartProducts = useSelector(state => state?.cart?.products) ?? [];
  const cartProductDetails = cartProducts?.find(p => p._id === id);
  const token = useSelector(state => state?.auth?.token);
  const relatedProducts = useSelector(state => state?.products?.productDetails?.relatedProducts);

  const [values, setValues] = useState({
    size: 'Medium',
    specialCakeNote: '',
  });
  const [product, setProduct] = useState({});
  const [images, setImages] = useState([]);
  const [selectedImg, setSelectedImg] = useState('');
  const [customizeOptions, setCustomizeOptions] = useState({});
  const [price, setPrice] = useState();
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);

  function scrollToProducts() {
    if (myDivRef.current) {
      myDivRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }

  useEffect(() => {
    if (!loading) {
      scrollToProducts();
    }
  }, [loading]);

  useEffect(() => {
    if (productDetails && productDetails?._id) {
      setProduct(productDetails);
      const sliderImages = productDetails?.files?.map((image, i) => ({
        original: `${image?.path}`,
        thumbnail: `${image?.path}`,
      }));
      setSelectedImg(sliderImages?.[0]?.thumbnail);
      setImages(sliderImages);
      if (productDetails?.price && !appMultipleOptions.includes(productDetails?.category)) {
        setPrice(productDetails?.price);
      } else if (productDetails?.isSinglePrice && productDetails?.price > 0) {
        setPrice(productDetails?.price);
      }
      setLoading(false);
    } else {
      setPrice();
    }
  }, [productDetails]);

  useEffect(() => {
    return () => {
      dispatch({ type: 'SINGLE_PRODUCT_DETAILS', payload: {} });
    };
  }, []);

  useEffect(() => {
    dispatch({ type: 'SINGLE_PRODUCT_DETAILS', payload: {} });
    dispatch(getPageContent('getSingleProduct', '', id));
    setValues({ ...values, quantity: cartProductDetails?.quantity ?? 1 });
    setLoading(true);
  }, [id]);

  const handleAddToCart = () => {
    dispatch(resetActionValues('CLEAR_COURSES', []));
    if (
      appMultipleOptions.includes(productDetails?.category) &&
      !productDetails?.isMultiplePrices &&
      !productDetails?.isSinglePrice
    ) {
      if (!customizeOptions?.flavour || !customizeOptions?.quantity) {
        return toast.error('Flavour & Quantity are required.');
      }

      let flavour = product?.customizeOptions?.find(f => f._id == customizeOptions?.flavour)?.name;
      let quantity = variants?.find(v => v?._id == customizeOptions?.quantity)?.quantity;
      let customizationOptions = {
        flavour,
        quantity,
      };
      let totalProductPrice = price;

      if (product?.customizeCharges) {
        totalProductPrice = price + product?.customizeCharges;
      }

      let cartProduct = {
        ...product,
        ...values,
        selectedImg,
        customizationOptions,
        price: totalProductPrice,
        originalPrice: price,
      };
      dispatch(addItemToCart(cartProduct, token));
      return;
    }

    if (!product?.price) {
      return toast.error('Please select a flavor to add to your cart.');
    }

    let cartProduct = { ...product, ...values, selectedImg };
    dispatch(addItemToCart(cartProduct, token));
  };

  const handleQuantityChange = e => {
    if (e?.target?.value < 1) {
      return;
    }
    setValues({ ...values, quantity: e?.target?.value });
  };

  const handlePriceSelect = e => {
    const selected = product?.priceOptions[e?.target?.value];
    if (selected) {
      setProduct({ ...product, price: selected?.price, selectedPrice: selected });
      setPrice(selected?.price);
    } else {
      setProduct({ ...product, price: '', selectedPrice: '' });
    }
  };

  const handleSlide = index => {
    setSelectedImg(images[index].original);
  };

  const handleCustomizationChange = e => {
    setCustomizeOptions({ ...customizeOptions, [e?.target?.name]: e?.target?.value });

    if (e?.target?.name === 'flavour') {
      const variants = productDetails?.customizeOptions?.find(
        v => v._id == e?.target?.value
      )?.variants;

      if (variants) {
        setVariants(variants);
        setPrice();
        setCustomizeOptions({
          ...customizeOptions,
          [e?.target?.name]: e?.target?.value,
          quantity: '',
        });
      } else {
        toast.warning('The selected flavour are not available. Please select other flavour.');
      }
    }

    if (e?.target?.name === 'quantity') {
      const priceDetails = variants?.find(o => o._id == e?.target?.value);
      setPrice(priceDetails?.price);
    }
  };

  const handleCheckoutCourse = () => {
    dispatch(resetActionValues('CLEAR_CART', []));

    let cartProduct = {
      ...product,
      ...values,
      quantity: 1,
    };

    dispatch(addItemToCart(cartProduct, token));

    navigate('/checkout');
  };

  const handleNoteChange = e => {
    if (e?.target?.value?.length > 25) return;
    setValues({ ...values, [e?.target?.name]: e?.target?.value });
  };

  const productTitle = product?.title
    ? `${product.title} – Order Custom Cake Online in Bangalore`
    : 'Product Details – SayCheeze Cakes';
  const productDescription =
    product?.shortDescription ||
    (product?.description
      ? `${product.description.replace(/<[^>]+>/g, '').slice(0, 155)}...`
      : 'View detailed information about this custom cake, its flavors, sizes, and customization options from SayCheeze Cakes in Bangalore.');

  const productImage = product?.files?.[0]?.path;
  const canonicalUrl = id
    ? `https://www.saycheezecakes.com/product/${id}`
    : 'https://www.saycheezecakes.com/shop';

  return !loading ? (
    <div>
      <Helmet>
        <title>{productTitle}</title>
        <meta name='description' content={productDescription} />
        {productImage && <meta property='og:image' content={productImage} />}
        <link rel='canonical' href={canonicalUrl} />
      </Helmet>

      <AppTitle
        title={'Product Details'}
        bgImg={aboutBg}
        from='Shop'
        to='Product Details'
        link={'/shop'}
      />

      <div className='section section-margin-top section-padding-03' ref={myDivRef}>
        <div className='container'>
          <div className='row'>
            <div className='col-lg-6 offset-lg-0 col-md-10 offset-md-1'>
              {images?.length > 1 ? (
                <div className={`product-details-img `}>
                  <div className={`order-2`}>
                    <ImageGallery
                      items={images}
                      thumbnailPosition={'left'}
                      showNav={false}
                      onSlide={handleSlide}
                    />
                  </div>
                </div>
              ) : (
                <div className='product-details-img d-flex overflow-hidden flex-row'>
                  <div className='single-product-vertical-tab  order-2'>
                    <div className='' id='popup-gallery'>
                      <img
                        className='w-100'
                        src={`${product?.files?.[0]?.path}`}
                        alt='Product'
                        loading='lazy'
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* <div className='swiper-button-vertical-next swiper-button-next'>
                    <i className='lastudioicon-arrow-right'></i>
                  </div>
                  <div className='swiper-button-vertical-prev swiper-button-prev'>
                    <i className='lastudioicon-arrow-left'></i>
                  </div> */}
            </div>

            <div className='col-lg-6'>
              <div className='product-summery position-relative'>
                <span className='product-head-price color_bg'>{product?.title}</span>
                <div className='product-head mb-3'>
                  {price ? (
                    <div>
                      <span className='product-head-price '>
                        {priceFormatter(price)}
                        {/* {product?.price && product?.price.toFixed(2)} */}
                      </span>
                    </div>
                  ) : (
                    <i className='color-warning'>
                      {customSelectTitles[product?.title?.trim()] ?? 'Please select flavour'} to
                      view price
                    </i>
                  )}

                  <div className='review-rating'>
                    {/* <span className='review-rating-bg'>
                      <span className='review-rating-active' style={{ width: '90%' }}></span>
                    </span>
                    <a href='#/' className='review-rating-text'>
                      (1 Review)
                    </a> */}
                  </div>
                </div>

                {product?.customizeCharges && product?.customizeCharges > 0 ? (
                  <p className='mb-2 color_bg'>
                    <b>Customize Charges: </b>₹{product?.customizeCharges?.toFixed(2)}
                  </p>
                ) : (
                  ''
                )}

                {product?.isMultiplePrices && product?.priceOptions && (
                  <div className='form-group mb-3'>
                    {/* <label>Select Price </label> */}
                    <div className='input-group'>
                      <select
                        className='form-control'
                        name='category'
                        // value={values?.['category']}
                        onChange={handlePriceSelect}
                      >
                        <option>
                          {product?.selectLabel ??
                            customSelectTitles[product?.title?.trim()] ??
                            'Pick your flavor'}
                        </option>
                        {/* <option>Select price option</option> */}
                        {product?.priceOptions?.map((option, i) => (
                          <option value={i} key={i}>
                            {option?.content} : ₹{option?.price.toFixed(2)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {product?.bufferingDays && product?.category !== 'Courses' ? (
                  <p className='mb-2'>
                    <b>Expected Delivery Date: </b>
                    {/* {moment().add(product?.bufferingDays, 'days').format('DD/MM/YYYY')} */}
                    {calculateExpectedDeliveryDate(product?.bufferingDays)}
                  </p>
                ) : (
                  ''
                )}

                {product?.customizeOptions?.length > 0 ? (
                  <>
                    <div className='form-group mb-3'>
                      <label>Select Flavor* </label>
                      <div className='input-group'>
                        <select
                          className='form-control'
                          name='flavour'
                          // value={values?.['category']}
                          onChange={handleCustomizationChange}
                        >
                          <option disabled selected>
                            Pick your flavor
                          </option>
                          {/* <option>Select price option</option> */}
                          {product?.customizeOptions?.map((option, i) => (
                            <option value={option?._id} key={i}>
                              {option?.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {variants?.length > 0 && (
                      <div className='form-group mb-3'>
                        <label>Select Quantity* </label>
                        <div className='input-group'>
                          <select
                            className='form-control'
                            name='quantity'
                            // value={values?.['category']}
                            value={customizeOptions?.['quantity']}
                            onChange={handleCustomizationChange}
                            disabled={variants?.length === 0}
                          >
                            <option disabled selected value={''}>
                              Check quantity
                            </option>
                            {/* <option>Select price option</option> */}
                            {variants?.map((option, i) => (
                              <option value={option?._id} key={i}>
                                {option?.quantity}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  ''
                )}

                <div
                  className='desc-content'
                  dangerouslySetInnerHTML={{ __html: product?.description }} // Display the content as HTML
                />
                {/* <p className='desc-content'>{product?.description}</p> */}

                {product?.category !== 'Courses' && (
                  <div>
                    {specialCakeNote?.map((element, i) => (
                      <div style={{ minWidth: '100%' }} className={'col-12'}>
                        {renderElements({
                          element,
                          setValues,
                          values,
                          handleChange: handleNoteChange,
                        })}
                      </div>
                    ))}

                    <ul
                      className='product-meta text-end'
                      style={{ marginBottom: -1, marginTop: -1 }}
                    >
                      <li className='product-meta-wrapper'>
                        <span
                          className={`product-meta-name ${
                            values?.specialCakeNote?.length > 24 && 'text-danger'
                          }`}
                        >
                          {values?.specialCakeNote?.length}/25
                        </span>
                      </li>
                    </ul>
                  </div>
                )}

                <ul className='product-cta'>
                  {product?.category !== 'Courses' && !onlyWhatsappOrders.includes(product?._id) ? (
                    <li>
                      <div className='quantity'>
                        <div className='cart-plus-minus'>
                          <div
                            className='dec qtybutton'
                            onClick={() =>
                              setValues({
                                ...values,
                                quantity: values?.quantity > 1 ? values?.quantity - 1 : 1,
                              })
                            }
                          >
                            -
                          </div>
                          <input
                            className='cart-plus-minus-box'
                            value={values?.quantity}
                            onChange={handleQuantityChange}
                          />
                          <div
                            className='inc qtybutton'
                            onClick={() => setValues({ ...values, quantity: values?.quantity + 1 })}
                          >
                            +
                          </div>
                        </div>
                      </div>
                    </li>
                  ) : (
                    ''
                  )}
                  <li>
                    {onlyWhatsappOrders.includes(product?._id) ? (
                      <>
                        <b>
                          <a href='https://wa.me/919945423080'>
                            Message us to book your order now.
                          </a>
                        </b>
                      </>
                    ) : (
                      <div className='cart-btn'>
                        <div className='add-to_cart'>
                          {product?.category === 'Courses' ? (
                            <button
                              className='btn btn-dark btn-hover-primary'
                              data-bs-toggle='modal'
                              data-bs-target='#modalCart'
                              onClick={handleCheckoutCourse}
                            >
                              Buy Now
                            </button>
                          ) : (
                            <button
                              className='btn btn-dark btn-hover-primary'
                              data-bs-toggle='modal'
                              data-bs-target='#modalCart'
                              onClick={handleAddToCart}
                              // disabled={!product?.isMultiplePrices ? false : !product?.price}
                            >
                              Add to cart
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </li>
                  <li>
                    <div className='actions'>
                      <a
                        href='https://wa.me/919945423080'
                        title='Wishlist'
                        className='action compare'
                        data-bs-toggle='modal'
                        data-bs-target='#modalWishlist'
                        target='_blank'
                      >
                        <i className='lastudioicon-b-whatsapp'></i>
                      </a>
                      {/* <a
                        href='#/'
                        title='Wishlist'
                        className='action compare'
                        data-bs-toggle='modal'
                        data-bs-target='#modalWishlist'
                      >
                        <i className='lastudioicon-heart-2'></i>
                      </a> */}
                      {/* <a
                        href='#/'
                        title='Compare'
                        className='action wishlist'
                        data-bs-toggle='modal'
                        data-bs-target='#modalCompare'
                      >
                        <i className='lastudioicon-ic_compare_arrows_24px'></i>
                      </a> */}
                    </div>
                  </li>
                </ul>

                <ul className='product-meta'>
                  <li className='product-meta-wrapper'>
                    <span className='product-meta-name'>Category:</span>
                    <span className='product-meta-detail'> {product?.category}</span>
                  </li>
                  {product?.subCategory && (
                    <li className='product-meta-wrapper'>
                      <span className='product-meta-name'>Subcategory: </span>
                      <span className='product-meta-detail'>{product?.subCategory}</span>
                    </li>
                  )}
                </ul>

                {/* <div className='product-share'>
                  <a href='#'>
                    <i className='lastudioicon-b-facebook'></i>
                  </a>
                  <a href='#'>
                    <i className='lastudioicon-b-twitter'></i>
                  </a>
                  <a href='#'>
                    <i className='lastudioicon-b-pinterest'></i>
                  </a>
                  <a href='#'>
                    <i className='lastudioicon-b-instagram'></i>
                  </a>
                </div>*/}
              </div>
            </div>
          </div>
        </div>
      </div>

      {relatedProducts && relatedProducts?.length > 0 && (
        <div className='section-padding-03 pt-0 mx-4'>
          <div className='container'>
            <div className='row'>
              <div className='col-12'>
                <div className='section-title'>
                  <h2 className='section-title__title'>Related Product</h2>
                </div>
              </div>

              <div className='swiper'>
                <Swiper
                  slidesPerView={3}
                  spaceBetween={30}
                  pagination={{
                    clickable: true,
                  }}
                  modules={[Pagination, Autoplay]}
                  className='swiper-wrapper'
                  autoplay={{
                    delay: 5000,
                    disableOnInteraction: false,
                  }}
                  breakpoints={{
                    0: {
                      slidesPerView: 1,
                      spaceBetween: 5,
                    },
                    640: {
                      slidesPerView: 1,
                      spaceBetween: 10,
                    },
                    768: {
                      slidesPerView: 2,
                      spaceBetween: 20,
                    },
                    1024: {
                      slidesPerView: 3,
                      spaceBetween: 30,
                    },
                  }}
                >
                  {relatedProducts?.map((product, i) => (
                    <SwiperSlide className='swiper-slide' key={i}>
                      <ProductCard product={product} />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  ) : (
    <AppLoader />
  );
};

export default ProductDetails;
